import { z } from "zod";
import { TopolColorSchema, TopolColorBearingSchema } from "./commonSchema";
import {
  TopolButtonBlockStyleAttributesSchema,
  TopolButtonBlockHoverAttributesSchema,
  TopolCarouselStyleAttributesSchema,
  TopolDividerBlockStyleAttributesSchema,
  TopolGifBlockStyleAttributesSchema,
  TopolImageAndVideoBlockStyleAttributesSchema,
  TopolLoopBlockStyleAttributesSchema,
  TopolSocialBlockStyleAttributesSchema,
  TopolTextBlockStyleAttributesSchema,
  TopolColumnStyleAttributesSchema,
  TopolSectionStyleAttributesSchema,
} from "./structures/sectionSchema";

export const defaultSettings = {
  "h1:color": "#000000",
  "h1:font-family": "Helvetica, sans-serif",
  "h2:color": "#000000",
  "h2:font-family": "Helvetica, Arial, sans-serif",
  "h3:color": "#000000",
  "h3:font-family": "Helvetica, Arial, sans-serif",
  ":color": "#000000",
  ":font-family": "Helvetica, Arial, sans-serif",
  ":line-height": "1.5",
  "a:color": "#24bfbc",
  "button:background-color": "#e85034",
  containerWidth: 600,
  templateBackgroundColor: "#FFFFFF",
  fonts: "Helvetica,Arial,sans-serif",
  "mj-text": {
    "line-height": 1.5,
  },
  "mj-button": {},
};

export const TopolSettingsSchema = z.object({
  "h1:color": z.string().catch(defaultSettings["h1:color"]),
  "h1:font-family": z.string().catch(defaultSettings["h1:font-family"]),
  "h2:color": z.string().catch(defaultSettings["h2:color"]),
  "h2:font-family": z.string().catch(defaultSettings["h2:font-family"]),
  "h3:color": z.string().catch(defaultSettings["h3:color"]),
  "h3:font-family": z.string().catch(defaultSettings["h3:font-family"]),
  ":color": z.string().catch(defaultSettings[":color"]),
  ":font-family": z.string().catch(defaultSettings[":font-family"]),
  ":line-height": z.string().catch(defaultSettings[":line-height"]),
  "a:color": z.string().catch(defaultSettings["a:color"]),
  "button:background-color": z
    .string()
    .catch(defaultSettings["button:background-color"]),
  containerWidth: z.number().catch(defaultSettings["containerWidth"]),
  templateBackgroundColor: z
    .string()
    .optional()
    .catch(defaultSettings["templateBackgroundColor"]),
  fonts: z.string().catch(defaultSettings["fonts"]),
  "mj-text": z
    .object({
      "line-height": z
        .number()
        .optional()
        .default(defaultSettings["mj-text"]["line-height"]),
      "font-family": z.string().optional().catch(undefined),
      ...TopolTextBlockStyleAttributesSchema.partial().shape,
    })
    .catch(defaultSettings["mj-text"]),
  "mj-button": z
    .object({
      ...TopolButtonBlockStyleAttributesSchema.partial().shape,
      hover: z
        .object({
          ...TopolButtonBlockHoverAttributesSchema.partial().shape,
        })
        .optional()
        .catch(undefined),
    })
    .catch(defaultSettings["mj-button"]),
  "mj-carousel": TopolCarouselStyleAttributesSchema.partial()
    .optional()
    .catch(undefined),
  "mj-divider": TopolDividerBlockStyleAttributesSchema.partial()
    .optional()
    .catch(undefined),
  "mj-gif": TopolGifBlockStyleAttributesSchema.partial()
    .optional()
    .catch(undefined),
  "mj-image": TopolImageAndVideoBlockStyleAttributesSchema.partial()
    .optional()
    .catch(undefined),
  "mj-loop": TopolLoopBlockStyleAttributesSchema.partial()
    .optional()
    .catch(undefined),
  "mj-social": TopolSocialBlockStyleAttributesSchema.partial()
    .optional()
    .catch(undefined),
  "mj-column": TopolColumnStyleAttributesSchema.partial()
    .optional()
    .catch(undefined),
  "mj-section": TopolSectionStyleAttributesSchema.partial()
    .optional()
    .catch(undefined),
  "mj-class": z
    .object({
      name: z.string(),
      "background-color": TopolColorSchema.optional().catch(undefined),
      gradient: TopolColorBearingSchema.optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
  "mj-preview": z
    .object({
      "background-color": TopolColorSchema.optional().catch(undefined),
      text: z.string().optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
  "mj-subject": z
    .object({
      text: z.string().optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
  "mj-spacer": z
    .object({
      height: z.string().optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
  "mj-rating": z
    .object({
      questionAttributes: z
        .object({
          color: TopolColorSchema.optional().catch(undefined),
          "font-size": z.string().optional().catch(undefined),
          "font-family": z.string().optional().catch(undefined),
          "font-weight": z.string().optional().catch(undefined),
          "font-style": z.string().optional().catch(undefined),
          "text-decoration": z.string().optional().catch(undefined),
          "text-transform": z.string().optional().catch(undefined),
          "letter-spacing": z.string().optional().catch(undefined),
          "line-height": z
            .union([z.string(), z.number()])
            .optional()
            .catch(undefined),
          padding: z.string().optional().catch(undefined),
        })
        .optional()
        .catch(undefined),
      captionsAttributes: z
        .object({
          color: TopolColorSchema.optional().catch(undefined),
          "font-size": z.string().optional().catch(undefined),
          "font-family": z.string().optional().catch(undefined),
          "font-weight": z.string().optional().catch(undefined),
          "font-style": z.string().optional().catch(undefined),
          "text-decoration": z.string().optional().catch(undefined),
          "text-transform": z.string().optional().catch(undefined),
          "letter-spacing": z.string().optional().catch(undefined),
          "line-height": z
            .union([z.string(), z.number()])
            .optional()
            .catch(undefined),
          padding: z.string().optional().catch(undefined),
          layout: z.string().optional().catch(undefined),
          layoutMobile: z.string().optional().catch(undefined),
        })
        .optional()
        .catch(undefined),
      segmentsAttributes: z
        .object({
          size: z.string().optional().catch(undefined),
          gap: z.string().optional().catch(undefined),
          autoWidth: z.boolean().optional().catch(undefined),
          color: TopolColorSchema.optional().catch(undefined),
          "font-size": z.string().optional().catch(undefined),
          "font-family": z.string().optional().catch(undefined),
          "font-weight": z.string().optional().catch(undefined),
          "font-style": z.string().optional().catch(undefined),
          "text-decoration": z.string().optional().catch(undefined),
          "text-transform": z.string().optional().catch(undefined),
          "background-color": TopolColorSchema.optional().catch(undefined),
          border: TopolColorBearingSchema.optional().catch(undefined),
          "border-radius": z.string().optional().catch(undefined),
        })
        .optional()
        .catch(undefined),
      imageLabelAttributes: z
        .object({
          color: TopolColorSchema.optional().catch(undefined),
          "font-size": z.string().optional().catch(undefined),
          "font-family": z.string().optional().catch(undefined),
          "font-weight": z.string().optional().catch(undefined),
          "font-style": z.string().optional().catch(undefined),
          "text-decoration": z.string().optional().catch(undefined),
          "text-transform": z.string().optional().catch(undefined),
        })
        .optional()
        .catch(undefined),
      align: z.string().optional().catch(undefined),
      padding: z.string().optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
});

export type TopolTemplateSettings = z.infer<typeof TopolSettingsSchema>;
